class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.lazyShellHTML = this.hasAttribute('data-lazy-cart-drawer') ? this.innerHTML : '';
    this.isLoaded =
      !this.hasAttribute('data-lazy-cart-drawer') || this.dataset.cartDrawerLoaded === 'true';
    this.loadingPromise = null;
    this.revealPromise = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[id*="cart-icon-bubble"]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    })
  }

  async open(triggeredBy, sectionHTML, forceReload = false) {
    const wasActive = this.classList.contains('active');
    const shouldLoadBeforeReveal =
      !wasActive && (!this.isLoaded || Boolean(sectionHTML) || forceReload);
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Keep the drawer hidden while a large cart is being prepared. Replacing
    // the lightweight shell during the slide-in transition makes it jump.
    if (!shouldLoadBeforeReveal) await this.showLoading(triggeredBy);

    try {
      await this.loadContents(sectionHTML, forceReload);
    } catch (error) {
      console.error('Cart drawer could not be loaded.', error);
      window.location.assign(window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart');
      return;
    }

    if (shouldLoadBeforeReveal) await this.showLoading(triggeredBy);

    if (!this.classList.contains('active')) {
      this.unloadContents();
      return;
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
      : this.querySelector('#CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.popup-modal__toggle');
    this.trapFocusAfterOpen(containerToTrapFocusOn, focusElement, wasActive);
  }

  showLoading(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) {
      this.classList.add('animate');
      return Promise.resolve();
    }
    if (this.revealPromise) return this.revealPromise;

    const shouldLockBody = document.querySelectorAll('.popup-modal.active').length === 0;
    this.revealPromise = new Promise((resolve) => {
      setTimeout(() => {
        // Match V5: activate and animate in the same deferred task.
        this.classList.add('active');
        this.classList.add('animate');
        resolve();
      });
    }).finally(() => {
      this.revealPromise = null;
    });

    if (shouldLockBody) {
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
    }

    return this.revealPromise;
  }

  beginLoading(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    return !this.isLoaded;
  }

  cancelLoading() {
    if (!this.isLoaded && this.classList.contains('active')) this.close();
  }

  trapFocusAfterOpen(container, focusElement, wasActive) {
    if (!container || !focusElement) return;

    const applyFocus = () => {
      if (!this.classList.contains('active') || !this.classList.contains('animate')) return;
      trapFocus(container, focusElement);
    };

    if (wasActive) {
      applyFocus();
      return;
    }

    let completed = false;
    const finish = () => {
      if (completed) return;
      completed = true;
      this.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimer);
      applyFocus();
    };
    const onTransitionEnd = () => finish();
    const fallbackTimer = setTimeout(finish, 600);

    // V36 moved focus only after the original slide-in transition ended.
    this.addEventListener('transitionend', onTransitionEnd, { once: true });
  }

  close() {
    this.classList.remove('animate');
    setTimeout(() => {
      if (this.classList.contains('animate')) return;
      this.classList.remove('active');
      if(document.querySelectorAll('.popup-modal.active').length == 0){
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
      this.unloadContents();
    }, 500);
    removeTrapFocus(this.activeElement);
  }

  loadContents(sectionHTML, forceReload = false) {
    if (sectionHTML) {
      this.hydrateContents(sectionHTML);
      return Promise.resolve();
    }

    if (this.isLoaded && !forceReload) return Promise.resolve();
    if (this.loadingPromise) {
      if (!forceReload) return this.loadingPromise;
      return this.loadingPromise
        .catch(() => {})
        .then(() => this.loadContents(sectionHTML, true));
    }

    const sectionURL =
      this.dataset.sectionUrl ||
      `${window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart'}?section_id=cart-drawer`;

    this.loadingPromise = fetch(sectionURL, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        this.hydrateContents(html);
      })
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  hydrateContents(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceDrawer = parsedHTML.querySelector('cart-drawer');
    if (!sourceDrawer) throw new Error('The cart-drawer section returned no drawer.');

    const wasActive = this.classList.contains('active');
    const wasAnimating = this.classList.contains('animate');

    this.innerHTML = sourceDrawer.innerHTML;
    this.className = sourceDrawer.className;
    if (wasActive) this.classList.add('active');
    if (wasAnimating) this.classList.add('animate');

    this.isLoaded = true;
    this.dataset.cartDrawerLoaded = 'true';
  }

  unloadContents() {
    if (!this.hasAttribute('data-lazy-cart-drawer') || !this.isLoaded || this.classList.contains('active')) return;

    this.innerHTML = this.lazyShellHTML;
    this.isLoaded = false;
    this.dataset.cartDrawerLoaded = 'false';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    const drawerSectionHTML = parsedState.sections && parsedState.sections['cart-drawer'];

    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-drawer') return;
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) return;

      const renderedHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      document.querySelectorAll(`[id="${section.id}"]`).forEach((sectionElement) => {
        const sectionTarget =
          section.selector && sectionElement.querySelector(section.selector)
            ? sectionElement.querySelector(section.selector)
            : sectionElement;
        if (renderedHTML !== null) sectionTarget.innerHTML = renderedHTML;
      });
      if(section.id == 'cart-free-delivery' && document.querySelector('#product-page-free-delivery')){
        document.querySelector('#product-page-free-delivery').innerHTML = renderedHTML || '';
      }
      if(section.id == 'cart-icon-bubble'){
        const parser = new DOMParser();
        const bubble = parser.parseFromString(renderedHTML || '', 'text/html');
        var ib = bubble.querySelector('.cart-count-bubble')?bubble.querySelector('.cart-count-bubble').innerHTML : '' ;
        document.querySelectorAll('.footer-sticky-nav__item__icon .cart-count-bubble').forEach((footerBubble) => {
          footerBubble.innerHTML = ib;
        });
      }
    });

    // Keep the Add to cart button spinner visible while the complete drawer
    // is prepared. Reveal the side cart only after every cart line is ready.
    try {
      await this.loadContents(drawerSectionHTML, !drawerSectionHTML);
    } catch (error) {
      console.error('Cart drawer could not be loaded.', error);
      window.location.assign(window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart');
      return;
    }

    await this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-bubble--mobile'
      },
      {
        id: 'cart-free-delivery'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble--mobile',
        section: 'cart-icon-bubble--mobile',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
